<template>
  <a-drawer
    :visible="visible"
    :width="drawerWidth"
    title="써마지 고객 마이그레이션"
    placement="right"
    @close="handleClose"
    class="thermage-migration-drawer"
  >
    <a-tabs v-model:activeKey="activeTab">
      <!-- 배치 마이그레이션 탭 -->
      <a-tab-pane key="batch" tab="배치 마이그레이션">
        <div class="tab-content">
          <a-alert
            message="THERMAGE 이벤트 참여자를 현재 이벤트로 일괄 마이그레이션합니다."
            description="전체 THERMAGE 참여자를 타겟 이벤트로 마이그레이션합니다. 이미 존재하는 참여자는 건너뜁니다."
            type="info"
            show-icon
            style="margin-bottom: 16px;"
          />

          <a-form layout="vertical">
            <a-form-item label="타겟 이벤트">
              <a-input :value="eventTitle" disabled />
              <div class="event-id-hint">ID: {{ targetEventId }}</div>
            </a-form-item>

            <a-form-item label="실행 모드">
              <a-radio-group v-model:value="batchDryRun">
                <a-radio :value="true">
                  <span>시뮬레이션 (Dry Run)</span>
                  <a-tooltip title="실제 변경 없이 결과만 미리보기">
                    <i class="fa fa-question-circle help-icon"></i>
                  </a-tooltip>
                </a-radio>
                <a-radio :value="false">실제 실행</a-radio>
              </a-radio-group>
            </a-form-item>
          </a-form>

          <!-- 결과 표시 영역 -->
          <div v-if="batchResult" class="result-section">
            <a-divider>실행 결과</a-divider>

            <a-row :gutter="16" class="result-summary">
              <a-col :span="6">
                <a-statistic
                  title="총 요청"
                  :value="batchResult.summary?.totalRequested || 0"
                />
              </a-col>
              <a-col :span="6">
                <a-statistic
                  title="생성"
                  :value="batchResult.summary?.created || 0"
                  :value-style="{ color: '#52c41a' }"
                />
              </a-col>
              <a-col :span="6">
                <a-statistic
                  title="스킵"
                  :value="batchResult.summary?.skipped || 0"
                  :value-style="{ color: '#faad14' }"
                />
              </a-col>
              <a-col :span="6">
                <a-statistic
                  title="실패"
                  :value="batchResult.summary?.failed || 0"
                  :value-style="{ color: '#ff4d4f' }"
                />
              </a-col>
            </a-row>

            <!-- 실패 목록 (있는 경우) -->
            <div v-if="batchResult.details?.failed?.length > 0" class="failed-list">
              <a-collapse>
                <a-collapse-panel header="실패 목록 상세보기">
                  <a-table
                    :dataSource="batchResult.details.failed"
                    :columns="failedColumns"
                    size="small"
                    :pagination="{ pageSize: 10 }"
                    :scroll="{ y: 200 }"
                  />
                </a-collapse-panel>
              </a-collapse>
            </div>
          </div>

          <div class="drawer-footer">
            <a-button @click="handleClose">닫기</a-button>
            <a-button
              type="primary"
              :loading="batchLoading"
              @click="executeBatchMigration"
              class="yellow-button"
            >
              {{ batchDryRun ? '시뮬레이션 실행' : '마이그레이션 실행' }}
            </a-button>
          </div>
        </div>
      </a-tab-pane>

      <!-- 단건 마이그레이션 탭 -->
      <a-tab-pane key="single" tab="단건 마이그레이션">
        <div class="tab-content">
          <a-alert
            message="특정 고객을 THERMAGE 참여자로 마이그레이션합니다."
            description="고객 이름과 전화번호를 입력하여 단건으로 마이그레이션할 수 있습니다."
            type="info"
            show-icon
            style="margin-bottom: 16px;"
          />

          <a-form
            :model="singleForm"
            :rules="singleFormRules"
            ref="singleFormRef"
            layout="vertical"
          >
            <a-form-item label="타겟 이벤트">
              <a-input :value="eventTitle" disabled />
              <div class="event-id-hint">ID: {{ targetEventId }}</div>
            </a-form-item>

            <a-form-item label="고객 이름" name="customerName">
              <a-input
                v-model:value="singleForm.customerName"
                placeholder="고객 이름을 입력하세요"
              />
            </a-form-item>

            <a-form-item label="전화번호" name="customerMobile">
              <a-input
                v-model:value="singleForm.customerMobile"
                placeholder="전화번호를 입력하세요 (예: 01012345678)"
              />
            </a-form-item>

            <a-form-item label="마케팅 수신 동의">
              <a-switch v-model:checked="singleForm.marketingConsent">
                <template #checkedChildren>동의</template>
                <template #unCheckedChildren>미동의</template>
              </a-switch>
            </a-form-item>
          </a-form>

          <!-- 단건 결과 표시 -->
          <div v-if="singleResult" class="result-section">
            <a-divider>실행 결과</a-divider>
            <a-result
              :status="singleResult.success ? 'success' : 'error'"
              :title="singleResult.success ? '마이그레이션 성공' : '마이그레이션 실패'"
              :sub-title="singleResult.message"
            >
              <template #extra v-if="singleResult.success">
                <div class="result-details">
                  <p><strong>액션:</strong> {{ getActionLabel(singleResult.action) }}</p>
                  <p v-if="singleResult.attendeeId"><strong>참여자 ID:</strong> {{ singleResult.attendeeId }}</p>
                  <p v-if="singleResult.customerId"><strong>고객 ID:</strong> {{ singleResult.customerId }}</p>
                </div>
              </template>
            </a-result>
          </div>

          <div class="drawer-footer">
            <a-button @click="handleClose">닫기</a-button>
            <a-button
              type="primary"
              :loading="singleLoading"
              @click="executeSingleMigration"
              class="yellow-button"
            >
              마이그레이션 실행
            </a-button>
          </div>
        </div>
      </a-tab-pane>
    </a-tabs>
  </a-drawer>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { message } from 'ant-design-vue';

export default {
  name: 'ThermageMigrationDrawer',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    targetEventId: {
      type: String,
      required: true
    },
    eventTitle: {
      type: String,
      default: ''
    },
    apiInstance: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'migration-complete'],
  setup(props, { emit }) {
    // Drawer width (responsive)
    const drawerWidth = computed(() => {
      if (typeof window !== 'undefined' && window.innerWidth <= 767) {
        return '100%';
      }
      return 520;
    });

    // Active tab
    const activeTab = ref('batch');

    // Batch migration states
    const batchDryRun = ref(true);
    const batchLoading = ref(false);
    const batchResult = ref(null);

    // Single migration states
    const singleFormRef = ref(null);
    const singleForm = ref({
      customerName: '',
      customerMobile: '',
      marketingConsent: false
    });
    const singleLoading = ref(false);
    const singleResult = ref(null);

    // Form validation rules
    const singleFormRules = {
      customerName: [
        { required: true, message: '고객 이름을 입력해주세요', trigger: 'blur' }
      ],
      customerMobile: [
        { required: true, message: '전화번호를 입력해주세요', trigger: 'blur' },
        { pattern: /^[0-9+\-\s]+$/, message: '올바른 전화번호 형식을 입력해주세요', trigger: 'blur' }
      ]
    };

    // Failed list columns
    const failedColumns = [
      { title: '이름', dataIndex: 'customerName', key: 'customerName', width: 100 },
      { title: '전화번호', dataIndex: 'customerMobile', key: 'customerMobile', width: 120 },
      { title: '사유', dataIndex: 'reason', key: 'reason' }
    ];

    // Reset states when drawer closes
    watch(() => props.visible, (newVal) => {
      if (!newVal) {
        batchResult.value = null;
        singleResult.value = null;
        singleForm.value = {
          customerName: '',
          customerMobile: '',
          marketingConsent: false
        };
      }
    });

    // Execute batch migration
    const executeBatchMigration = async () => {
      if (!props.targetEventId) {
        message.warning('타겟 이벤트가 선택되지 않았습니다.');
        return;
      }

      batchLoading.value = true;
      batchResult.value = null;

      try {
        await props.apiInstance.migrateThermageAttendeesBatch(
          {
            targetEventId: props.targetEventId,
            dryRun: batchDryRun.value
          },
          (response) => {
            batchResult.value = response.data || response;
            const action = batchDryRun.value ? '시뮬레이션' : '마이그레이션';
            message.success(`${action}이 완료되었습니다.`);

            if (!batchDryRun.value) {
              emit('migration-complete');
            }
          },
          (error) => {
            message.error(`마이그레이션 실패: ${error.response?.data?.message || error.message}`);
          }
        );
      } finally {
        batchLoading.value = false;
      }
    };

    // Execute single migration
    const executeSingleMigration = async () => {
      if (!props.targetEventId) {
        message.warning('타겟 이벤트가 선택되지 않았습니다.');
        return;
      }

      // Validate form
      try {
        await singleFormRef.value.validate();
      } catch (e) {
        return;
      }

      singleLoading.value = true;
      singleResult.value = null;

      try {
        await props.apiInstance.migrateThermageAttendee(
          {
            targetEventId: props.targetEventId,
            customerName: singleForm.value.customerName,
            customerMobile: singleForm.value.customerMobile,
            marketingConsent: singleForm.value.marketingConsent
          },
          (response) => {
            singleResult.value = response.data || response;
            message.success('마이그레이션이 완료되었습니다.');
            emit('migration-complete');
          },
          (error) => {
            singleResult.value = {
              success: false,
              message: error.response?.data?.message || error.message
            };
            message.error(`마이그레이션 실패: ${error.response?.data?.message || error.message}`);
          }
        );
      } finally {
        singleLoading.value = false;
      }
    };

    // Get action label
    const getActionLabel = (action) => {
      const labels = {
        created: '신규 생성',
        updated: '세그먼트 추가',
        skipped: '이미 존재 (스킵)'
      };
      return labels[action] || action;
    };

    // Handle close
    const handleClose = () => {
      emit('close');
    };

    return {
      drawerWidth,
      activeTab,
      batchDryRun,
      batchLoading,
      batchResult,
      singleFormRef,
      singleForm,
      singleFormRules,
      singleLoading,
      singleResult,
      failedColumns,
      executeBatchMigration,
      executeSingleMigration,
      getActionLabel,
      handleClose
    };
  }
};
</script>

<style lang="scss" scoped>
.thermage-migration-drawer {
  .tab-content {
    padding-bottom: 60px;
  }

  .event-id-hint {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
  }

  .help-icon {
    margin-left: 4px;
    color: #999;
    cursor: help;
  }

  .result-section {
    margin-top: 16px;
  }

  .result-summary {
    margin-bottom: 16px;

    .ant-statistic {
      text-align: center;
    }
  }

  .failed-list {
    margin-top: 16px;
  }

  .result-details {
    text-align: left;
    padding: 16px;
    background: #fafafa;
    border-radius: 4px;

    p {
      margin-bottom: 8px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .drawer-footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px 24px;
    background: #fff;
    border-top: 1px solid #e8e8e8;
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .yellow-button {
    background-color: #fadb14 !important;
    border-color: #fadb14 !important;
    color: #000 !important;

    &:hover {
      background-color: #ffc53d !important;
      border-color: #ffc53d !important;
    }
  }
}
</style>
